/**
 * GuidedCourseConfigService
 *
 * Fetches and applies guided course UI configuration from the backend.
 * This service consolidates all guided course-related UI logic by:
 * - Fetching configuration for disabled features
 * - Applying disabled states to form elements
 * - Showing help texts and override warnings
 * - Providing language-specific configuration
 *
 * Usage:
 *   const configService = new GuidedCourseConfigService(csrfToken);
 *   await configService.fetchConfig('guided', { voice_provider: 'elevenlabs', voice: 'natalia_female_elevenlabs' });
 *   configService.applyToUI();
 *
 * Integration with ChatOptions:
 *   The ChatOptions modal is built dynamically with conditional rendering based on
 *   `disableGuidedMode` and `disableAiModelSelection` flags. These flags are set
 *   at initialization time from chat.js. This service provides:
 *   1. Runtime updates when topic changes during a session
 *   2. Language config for ChatService (non-Latin handling, transliteration)
 *   3. Feature flags for guided/grammar/vocab course detection
 */
class GuidedCourseConfigService {
  constructor(csrfToken) {
    this.csrfToken = csrfToken;
    this.config = null;
    this.previousTopicCategory = null;
    this.chatOptions = null; // Reference to ChatOptions for runtime updates
  }

  /**
   * Escape HTML special characters to prevent XSS
   * @param {string} text - Text to escape
   * @returns {string} Escaped text safe for HTML insertion
   */
  escapeHtml(text) {
    if (text === null || text === undefined) return '';
    const div = document.createElement('div');
    div.textContent = String(text);
    return div.innerHTML;
  }

  /**
   * Set reference to ChatOptions for runtime updates
   * @param {ChatOptions} chatOptions - The ChatOptions instance
   */
  setChatOptions(chatOptions) {
    this.chatOptions = chatOptions;
  }

  /**
   * Fetch configuration for a topic category
   * @param {string} topicCategory - The topic category (e.g., 'guided', 'grammar', 'vocab')
   * @param {Object} currentSettings - Current chat settings
   * @returns {Promise<Object>} Configuration object
   */
  async fetchConfig(topicCategory, currentSettings = {}) {
    // Skip fetch if topic hasn't changed
    if (this.config && this.previousTopicCategory === topicCategory) {
      return this.config;
    }

    try {
      const response = await fetch('/langua/chat_config/guided_course', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken
        },
        body: JSON.stringify({
          topic_category: topicCategory,
          ...currentSettings
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch config: ${response.status}`);
      }

      const result = await response.json();
      this.config = result.data;
      this.previousTopicCategory = topicCategory;
      return this.config;
    } catch (error) {
      console.error('Error fetching guided course config:', error);
      // Return a safe default config
      return this.getDefaultConfig();
    }
  }

  /**
   * Get topic info without full config fetch (lighter weight)
   * @param {string} topicCategory - The topic category
   * @returns {Promise<Object>} Topic info object
   */
  async getTopicInfo(topicCategory) {
    try {
      const response = await fetch(`/langua/chat_config/topic_info?topic_category=${encodeURIComponent(topicCategory)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch topic info: ${response.status}`);
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error('Error fetching topic info:', error);
      return null;
    }
  }

  /**
   * Apply UI changes based on the fetched configuration.
   * This method handles two scenarios:
   * 1. Updates ChatOptions flags for runtime changes (modal will rebuild with new settings)
   * 2. Updates existing DOM elements with data-feature attributes (if present)
   */
  applyToUI() {
    if (!this.config) return;

    // Update ChatOptions if available (for runtime topic changes)
    this.applyChatOptionsFlags();

    // Clear any previous UI modifications
    this.clearPreviousUIState();

    // Disable features via data-feature attributes
    this.applyDisabledFeatures();

    // Show help texts
    this.applyHelpTexts();

    // Show help texts in main chat options display
    this.applyMainDisplayHelpTexts();

    // Show override warnings
    this.applyOverrideWarnings();

    // Update existing modal elements if modal is open
    this.updateExistingModal();
  }

  /**
   * Update ChatOptions flags based on config
   * This ensures the modal will be built correctly on next open
   */
  applyChatOptionsFlags() {
    if (!this.chatOptions || !this.config) return;

    const disabledFeatures = this.config.disabled_features || [];

    // Update guided mode flag
    if (disabledFeatures.includes('guided_mode')) {
      this.chatOptions.disableGuidedMode = true;
      this.chatOptions.guidedMode = false;
    }

    // Update AI model selection flag
    if (disabledFeatures.includes('ai_model_select')) {
      this.chatOptions.disableAiModelSelection = true;
    }
  }

  /**
   * Update elements in an already-open modal
   */
  updateExistingModal() {
    const modal = document.getElementById('chatOptionsModal');
    if (!modal) return;

    const disabledFeatures = this.config.disabled_features || [];

    // Disable guided mode checkbox if feature is disabled
    if (disabledFeatures.includes('guided_mode')) {
      const guidedModeCheckbox = document.getElementById('chatGuidedMode');
      if (guidedModeCheckbox) {
        guidedModeCheckbox.disabled = true;
        guidedModeCheckbox.checked = false;
      }
    }

    // Disable AI model select if feature is disabled
    if (disabledFeatures.includes('ai_model_select')) {
      const aiModelSelect = document.getElementById('chatAiModel');
      if (aiModelSelect) {
        aiModelSelect.disabled = true;
        // Add help text below the select
        const helpText = aiModelSelect.parentElement?.querySelector('.form-text');
        if (helpText) {
          helpText.textContent = 'For this chat to work optimally, a custom model is used.';
        }
      }
    }
  }

  /**
   * Clear previous UI modifications when topic changes
   */
  clearPreviousUIState() {
    // Remove disabled states
    document.querySelectorAll('[data-feature].guided-disabled').forEach(element => {
      element.classList.remove('guided-disabled');
      element.disabled = false;
    });

    // Hide help texts
    document.querySelectorAll('[data-help-for]').forEach(element => {
      element.style.display = 'none';
      element.textContent = '';
    });

    // Remove override warnings
    document.querySelectorAll('.guided-override-warning').forEach(element => {
      element.remove();
    });

    // Remove main display help texts
    const helpTextsContainer = document.querySelector('.js-course-help-texts');
    if (helpTextsContainer) {
      helpTextsContainer.remove();
    }
  }

  /**
   * Apply disabled states to features
   */
  applyDisabledFeatures() {
    if (!this.config.disabled_features) return;

    this.config.disabled_features.forEach(feature => {
      this.disableFeature(feature);
    });
  }

  /**
   * Disable a specific feature element
   * @param {string} feature - Feature name (e.g., 'guided_mode', 'auto_translate')
   */
  disableFeature(feature) {
    const element = document.querySelector(`[data-feature="${feature}"]`);
    if (element) {
      element.classList.add('guided-disabled');

      // Disable the actual input/select within the element
      const input = element.querySelector('input, select, textarea');
      if (input) {
        input.disabled = true;
      }
    }
  }

  /**
   * Apply help texts to UI elements
   */
  applyHelpTexts() {
    if (!this.config.help_texts) return;

    Object.entries(this.config.help_texts).forEach(([setting, text]) => {
      this.showHelpText(setting, text);
    });
  }

  /**
   * Show help text for a setting
   * @param {string} setting - Setting name
   * @param {string} text - Help text content
   */
  showHelpText(setting, text) {
    const helpElement = document.querySelector(`[data-help-for="${setting}"]`);
    if (helpElement) {
      helpElement.textContent = text;
      helpElement.style.display = 'block';
    }
  }

  /**
   * Apply help texts to the main chat options display area
   * This adds help text BELOW the "You'll be chatting with..." text for guided/grammar courses
   */
  applyMainDisplayHelpTexts() {
    if (!this.config || !this.config.help_texts) return;

    const helpTexts = this.config.help_texts;
    const topicCategory = this.previousTopicCategory;

    // Find the main chat options display container
    const displayContainer = document.querySelector('.js-display-chat-options');
    if (!displayContainer) return;

    // Check if this is a guided course (has guided_mode and auto_translate disabled)
    const isGuidedCourse = topicCategory === 'guided' ||
      (helpTexts.guided_mode && helpTexts.auto_translate);

    // Check if this is a grammar course
    const isGrammarCourse = topicCategory === 'grammar' ||
      topicCategory?.startsWith('grammar') ||
      (helpTexts.auto_correct && !helpTexts.auto_translate);

    if (isGuidedCourse) {
      // For guided courses: Show help text below the voice info
      this.showGuidedCourseHelpText(displayContainer);
    } else if (isGrammarCourse) {
      // For grammar courses: Show note about auto-correct
      this.showGrammarCourseHelpText(displayContainer);
    }
  }

  /**
   * Restore the original display text (after navigating away from course)
   * @param {Element} displayContainer - The display container element
   * @param {string} originalText - The original "You'll be chatting with..." text
   */
  restoreDisplayText(displayContainer, originalText) {
    if (!displayContainer) return;

    // Remove any help texts
    const helpTextsContainer = displayContainer.querySelector('.js-course-help-texts');
    if (helpTextsContainer) {
      helpTextsContainer.remove();
    }

    // Restore original span - first check for direct child span
    let displaySpan = displayContainer.querySelector(':scope > span');
    if (!displaySpan) {
      displaySpan = document.createElement('span');
      displayContainer.appendChild(displaySpan);
    }

    // Make sure the span is visible (it may have been hidden by showGuidedCourseHelpText)
    displaySpan.style.display = '';
    displaySpan.textContent = originalText;
  }

  /**
   * Show guided course help text with lightbulb icon (after click)
   * Hides the "You'll be chatting with..." text and shows help text instead
   */
  showGuidedCourseHelpText(displayContainer) {
    // Hide existing span instead of destroying it (so it can be restored later)
    const existingSpan = displayContainer.querySelector(':scope > span');
    if (existingSpan) {
      existingSpan.style.display = 'none';
    }

    // Remove any existing help texts container first
    const existingHelpTexts = displayContainer.querySelector('.js-course-help-texts');
    if (existingHelpTexts) {
      existingHelpTexts.remove();
    }

    // Create help texts container
    const helpTextsContainer = document.createElement('div');
    helpTextsContainer.className = 'js-course-help-texts';

    helpTextsContainer.innerHTML = `
      <div class="d-flex align-items-start justify-content-center">
        <div class="guided-course-help-text-wrapper">
          <div class="mb-2">
            <i class="fa fa-lightbulb-o" style="color: #ffc107; margin-right: 8px; font-size: 1.1em;"></i>
            <span style="color: #495057; font-size: 15px; font-weight: 600;">Optimized for learning</span>
          </div>
          <p style="color: #6c757d; font-size: 14px; margin: 0; line-height: 1.5;">
            These lessons use an AI teacher trained for the course who can seamlessly switch between languages. Auto-translate and auto-correct are disabled as the teacher provides translations and corrections.
          </p>
        </div>
      </div>
    `;

    displayContainer.appendChild(helpTextsContainer);
  }

  /**
   * Show grammar course help text (after click)
   * Hides the "You'll be chatting with..." text and shows help text instead
   */
  showGrammarCourseHelpText(displayContainer) {
    // Hide existing span instead of destroying it (so it can be restored later)
    const existingSpan = displayContainer.querySelector(':scope > span');
    if (existingSpan) {
      existingSpan.style.display = 'none';
    }

    // Remove any existing help texts container first
    const existingHelpTexts = displayContainer.querySelector('.js-course-help-texts');
    if (existingHelpTexts) {
      existingHelpTexts.remove();
    }

    // Create help texts container
    const helpTextsContainer = document.createElement('div');
    helpTextsContainer.className = 'js-course-help-texts';

    helpTextsContainer.innerHTML = `
      <div class="d-flex justify-content-center">
        <div class="guided-course-help-text-wrapper">
          <p style="color: #6c757d; font-size: 14px; margin: 0; line-height: 1.5;">
            <strong style="color: #495057;">Note:</strong> Auto-correct is disabled here as corrections are provided within the exercises.
          </p>
        </div>
      </div>
    `;

    displayContainer.appendChild(helpTextsContainer);
  }

  /**
   * Apply override warning messages
   */
  applyOverrideWarnings() {
    if (!this.config.overrides || this.config.overrides.length === 0) return;

    this.config.overrides.forEach(override => {
      this.showOverrideWarning(override);
    });
  }

  /**
   * Show an override warning for a setting
   * @param {Object} override - Override info { setting, current_value, override_value, reason }
   */
  showOverrideWarning(override) {
    // Escape setting value for safe use in selectors and attributes
    const escapedSetting = this.escapeHtml(override.setting);
    const settingElement = document.querySelector(`[name*="${CSS.escape(override.setting)}"]`);
    if (!settingElement) return;

    const formGroup = settingElement.closest('.form-group') || settingElement.parentElement;
    if (!formGroup) return;

    // Check if warning already exists
    if (formGroup.querySelector(`.guided-override-warning[data-setting="${CSS.escape(override.setting)}"]`)) {
      return;
    }

    // Create warning element using DOM methods to prevent XSS
    const warningDiv = document.createElement('div');
    warningDiv.className = 'alert alert-info guided-override-warning mt-2';
    warningDiv.setAttribute('data-setting', override.setting);

    const small = document.createElement('small');

    const icon = document.createElement('i');
    icon.className = 'fa fa-info-circle';
    small.appendChild(icon);
    small.appendChild(document.createTextNode(' '));

    const strong = document.createElement('strong');
    strong.textContent = 'Note:';
    small.appendChild(strong);
    small.appendChild(document.createTextNode(' ' + (override.reason || '')));

    if (override.override_name) {
      small.appendChild(document.createElement('br'));
      const em = document.createElement('em');
      em.textContent = 'Using: ' + override.override_name;
      small.appendChild(em);
    }

    warningDiv.appendChild(small);
    formGroup.appendChild(warningDiv);
  }

  /**
   * Check if a feature is disabled for current topic
   * @param {string} feature - Feature name
   * @returns {boolean}
   */
  isFeatureDisabled(feature) {
    if (!this.config || !this.config.disabled_features) return false;
    return this.config.disabled_features.includes(feature);
  }

  /**
   * Check if current topic is a guided course
   * @returns {boolean}
   */
  isGuidedCourse() {
    return this.config && this.config.disabled_features &&
           this.config.disabled_features.includes('guided_mode') &&
           this.config.disabled_features.includes('auto_translate');
  }

  /**
   * Check if current topic is a grammar course
   * @returns {boolean}
   */
  isGrammarCourse() {
    return this.config && this.config.disabled_features &&
           this.config.disabled_features.includes('auto_correct') &&
           this.config.disabled_features.includes('ai_model_select');
  }

  /**
   * Get language-specific configuration
   * @returns {Object|null}
   */
  getLanguageConfig() {
    return this.config ? this.config.language_config : null;
  }

  /**
   * Check if word timing needs special handling for non-Latin scripts
   * @returns {boolean}
   */
  needsNonLatinWordTiming() {
    const langConfig = this.getLanguageConfig();
    return langConfig ? langConfig.needs_word_timing : false;
  }

  /**
   * Check if language uses non-Latin script
   * @returns {boolean}
   */
  isNonLatinLanguage() {
    const langConfig = this.getLanguageConfig();
    return langConfig ? langConfig.is_non_latin : false;
  }

  /**
   * Get transliteration mode for current language
   * @returns {string|null} 'no_transliteration', 'add_transliteration_with_break', or 'none'
   */
  getTransliterationMode() {
    const langConfig = this.getLanguageConfig();
    return langConfig ? langConfig.transliteration_mode : null;
  }

  /**
   * Check if guided course option should be shown to user
   * @returns {boolean}
   */
  shouldShowGuidedCourse() {
    return this.config ? this.config.show_guided_course : true;
  }

  /**
   * Get default config when API is unavailable
   * @returns {Object}
   */
  getDefaultConfig() {
    return {
      disabled_features: [],
      help_texts: {},
      show_guided_course: true,
      overrides: [],
      language_config: {
        is_non_latin: false,
        needs_word_timing: false,
        transliteration_mode: 'none'
      }
    };
  }

  /**
   * Reset the service state
   */
  reset() {
    this.config = null;
    this.previousTopicCategory = null;
    this.clearPreviousUIState();
  }
}

export default GuidedCourseConfigService;
